/**
 * «La giornata sulle macchine»: una traccia per macchina, i blocchi larghi quanto
 * durano, nell'ordine in cui il piano li mette.
 *
 * È il pezzo che rende M4 un'immagine invece di un elenco: si vede quale macchina
 * è piena e quale è ferma **senza leggere un numero**.
 *
 * Tre scelte che il dato impone, e che non vanno «migliorate» via:
 *
 * 1. **È la giornata in sequenza, non un orario.** `piano_op.data_inizio` è nullo
 *    su tutte le righe: abbiamo la durata e l'ordine, non l'ora di inizio. L'asse
 *    lo dice, altrimenti mentirebbe.
 * 2. **Il colore non distingue lo stadio** — quello lo dicono già il gruppo e
 *    l'etichetta. Le due tinte verdi provate nel mockup erano indistinguibili
 *    (ΔE 14,5 in visione normale, sotto il minimo). Il colore è libero per la cosa
 *    che conta: **ha i materiali o no**. E «senza materiali» è una **marcatura** sul
 *    lavoro, non un lavoro d'altro colore: nella giornata del 28 luglio 100
 *    lavorazioni su 106 hanno il flag, e riempirle tutte d'ambra faceva sembrare la
 *    giornata un allarme unico invece di lavoro con un dubbio sopra.
 * 3. **Le macchine ferme hanno la loro traccia.** L'inattività è un'informazione:
 *    mostrare solo chi lavora avrebbe nascosto che quattro linee su nove sono
 *    spente il giorno della riapertura.
 *
 * E il **limite del giorno**: dove la somma delle durate supera le 24 ore la traccia
 * lo segna. Sul piano del 28 luglio quattro macchine-giorno lo sfondano (Termal Twin
 * 35,0 h il 24 agosto, Concetti 30,2 h il 28 luglio) — un'impossibilità aritmetica
 * dichiarata dal MES che nessuna schermata mostrava.
 */

import { AlertTriangle } from "lucide-react";

import { cn } from "@/lib/utils";
import { dec, kg, minutiInOre } from "@/lib/numeri";
import type { M4GiornataResponse, M4Traccia } from "@/types/api";

const MINUTI_AL_GIORNO = 24 * 60;

/** Sotto questa quota il blocco è troppo stretto per contenere il nome. */
const QUOTA_MINIMA_TESTO = 7;

export function GiornataMacchine({ dati }: { dati: M4GiornataResponse }) {
  const vuota = dati.totali.lavorazioni === 0;
  const massimo = Math.max(...dati.tracce.map((t) => t.durata_minuti), 60);
  const sfonda = massimo > MINUTI_AL_GIORNO;

  const estrusori = dati.tracce.filter((t) => t.gruppo === "estrusione");
  const confezionatrici = dati.tracce.filter((t) => t.gruppo === "confezionamento");

  return (
    <section className="rounded-xl border bg-card">
      <header className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b px-4 py-3">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.11em]">
          La giornata sulle macchine
        </h2>
        {!vuota && (
          <p className="text-[11px] text-muted-foreground">
            la giornata in sequenza, non un orario: il piano dà la durata e l'ordine, non
            l'ora di inizio
          </p>
        )}
        <div className="ml-auto flex flex-wrap gap-x-4 gap-y-1 text-[10.5px] text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <i className="size-2.5 rounded-sm bg-[var(--m4-lavoro)]" />
            ha i materiali
          </span>
          <span className="flex items-center gap-1.5">
            <i className="size-2.5 rounded-sm bg-[var(--m4-lavoro)] [background-image:repeating-linear-gradient(45deg,transparent_0_2px,var(--m4-manca)_2px_5px)] ring-1 ring-inset ring-[var(--m4-manca)]" />
            tratteggiato: il MES dice senza materiali
          </span>
        </div>
      </header>

      {vuota ? (
        <div className="px-4 py-10 text-center">
          <p className="text-lg font-semibold">Nessuna lavorazione in piano</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Le {dati.tracce.length} macchine sono ferme. Il piano non ha righe in questa
            giornata.
          </p>
        </div>
      ) : (
        <div className="space-y-1 px-4 py-3">
          <Scala massimo={massimo} sfonda={sfonda} />
          <Gruppo titolo="Estrusione — il semilavorato va nei silos" />
          {estrusori.map((t) => (
            <Traccia key={t.macchina} traccia={t} massimo={massimo} sfonda={sfonda} />
          ))}
          <div className="my-2 h-px bg-border" />
          <Gruppo titolo="Confezionamento — dai silos al magazzino" />
          {confezionatrici.map((t) => (
            <Traccia key={t.macchina} traccia={t} massimo={massimo} sfonda={sfonda} />
          ))}
        </div>
      )}

      <footer className="flex flex-wrap gap-x-6 gap-y-1.5 border-t px-4 py-2.5 text-[11px] text-muted-foreground">
        {!vuota && (
          <>
            <span>
              Estruso <b className="text-foreground">{kg(dati.totali.estrusione_kg)}</b> ·
              confezionato{" "}
              <b className="text-foreground">{kg(dati.totali.confezionamento_kg)}</b> → saldo
              silos{" "}
              <b
                className={
                  parseFloat(dati.saldo_silos_kg) < 0
                    ? "text-[var(--m4-manca-ink)]"
                    : "text-emerald-700"
                }
              >
                {parseFloat(dati.saldo_silos_kg) < 0 ? "−" : "+"}
                {kg(Math.abs(parseFloat(dati.saldo_silos_kg)))}
              </b>
            </span>
            <span>
              Più <b className="text-foreground">{dati.etichettatura.lavorazioni} etichettature</b>
              , che non si possono disegnare: il MES dichiara la durata solo su{" "}
              {dati.etichettatura.con_durata}
            </span>
          </>
        )}
        {dati.macchine_oltre_giornata.length > 0 && (
          <span className="flex items-start gap-1.5 text-[var(--m4-manca-ink)]">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            <span>
              Il piano chiede a{" "}
              {dati.macchine_oltre_giornata.length === 1
                ? "una macchina"
                : `${dati.macchine_oltre_giornata.length} macchine`}{" "}
              più ore di quante il giorno ne abbia:{" "}
              <b>
                {dati.macchine_oltre_giornata
                  .map((m) => `${m.macchina} ${dec(m.minuti / 60)} h`)
                  .join(" · ")}
              </b>
            </span>
          </span>
        )}
        {vuota && (
          <span>
            La fermata è dichiarata dal piano stesso, non dedotta: <b>zero righe</b> in questa
            giornata.
          </span>
        )}
      </footer>
    </section>
  );
}

function Gruppo({ titolo }: { titolo: string }) {
  return (
    <p className="pt-1.5 text-[9.5px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
      {titolo}
    </p>
  );
}

function Scala({ massimo, sfonda }: { massimo: number; sfonda: boolean }) {
  const oreTotali = Math.ceil(massimo / 60);
  const passo = Math.max(1, Math.round(oreTotali / 8));
  const tacche: number[] = [];
  for (let h = 0; h <= oreTotali; h += passo) {
    if (sfonda && h === 24) continue; // lo dice l'etichetta del limite
    tacche.push(h);
  }
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-x-3">
      <span />
      <div className="relative h-4">
        {tacche.map((h) => (
          <span
            key={h}
            className="absolute -translate-x-1/2 text-[9.5px] text-muted-foreground"
            style={{ left: `${((h * 60) / massimo) * 100}%` }}
          >
            {h}h
          </span>
        ))}
        {sfonda && (
          <span
            className="absolute -translate-x-1/2 whitespace-nowrap text-[9.5px] font-semibold text-[var(--m4-manca-ink)]"
            style={{ left: `${((24 * 60) / massimo) * 100}%` }}
          >
            24h · il giorno finisce qui
          </span>
        )}
      </div>
    </div>
  );
}

function Traccia({
  traccia,
  massimo,
  sfonda,
}: {
  traccia: M4Traccia;
  massimo: number;
  sfonda: boolean;
}) {
  return (
    <div className="grid grid-cols-[7rem_1fr] items-center gap-x-3">
      <div className="min-w-0 text-right">
        <p className="truncate text-[11.5px]">{traccia.macchina}</p>
        <p
          className={cn(
            "text-[9.5px] text-muted-foreground",
            traccia.oltre_la_giornata && "font-semibold text-[var(--m4-manca-ink)]",
          )}
        >
          {traccia.lavorazioni.length === 0
            ? "—"
            : `${traccia.lavorazioni.length} lav · ${minutiInOre(traccia.durata_minuti)}`}
          {traccia.oltre_la_giornata && " ⚠"}
        </p>
      </div>
      <div className="relative flex h-[26px] gap-0.5 overflow-hidden rounded-md bg-muted p-0.5">
        {traccia.lavorazioni.length === 0 ? (
          <span className="self-center pl-2 text-[10px] text-muted-foreground/70">ferma</span>
        ) : (
          traccia.lavorazioni.map((l, i) => {
            const quota = (l.durata_minuti / massimo) * 100;
            return (
              <span
                key={`${l.prodotto}-${i}`}
                title={`${l.prodotto} — ${dec(l.durata_minuti, 0)} minuti${
                  l.senza_materiali ? " · il MES dice: senza materiali" : ""
                }`}
                style={{ width: `${quota}%` }}
                className={cn(
                  "flex min-w-[3px] items-center overflow-hidden whitespace-nowrap rounded-[3px] px-1.5",
                  "bg-[var(--m4-lavoro)] text-[9.5px] text-[var(--m4-lavoro-ink)]",
                  l.senza_materiali &&
                    "[background-image:repeating-linear-gradient(45deg,transparent_0_3px,var(--m4-manca)_3px_7px)] ring-[1.5px] ring-inset ring-[var(--m4-manca)]",
                )}
              >
                {quota > QUOTA_MINIMA_TESTO ? l.prodotto : ""}
              </span>
            );
          })
        )}
        {sfonda && (
          <span
            className="pointer-events-none absolute -top-1 bottom-[-4px] border-l-[1.5px] border-dashed border-[var(--m4-manca)]"
            style={{ left: `${((24 * 60) / massimo) * 100}%` }}
          />
        )}
      </div>
    </div>
  );
}
